import { useCallback, useEffect, type FC } from 'react';
import { Field } from 'react-final-form';

import type { ApplicantLoginFormValues } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/types';
import { PUSH_CODE_FIELD } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/utils';
import type { TwoFactor } from 'src/models/twoFactor';

interface IframeEvent {
    eventId: 'PushTokenReceive';
    payload: string;
}

interface TwoFactorPushFieldProps extends Pick<TwoFactor, 'pushExternalAccessToken' | 'pushRedirectUrl'> {
    onFieldChange: (value: string) => void;
    onSubmit: () => Promise<void>;
}

const TwoFactorPushField: FC<TwoFactorPushFieldProps> = ({
    pushRedirectUrl,
    pushExternalAccessToken,
    onFieldChange,
    onSubmit,
}) => {
    const handleMessageReceive = useCallback(
        async (event: MessageEvent) => {
            if (typeof event.data !== 'string') {
                return;
            }

            try {
                const data = JSON.parse(event.data) as IframeEvent;

                if (data.eventId === 'PushTokenReceive') {
                    onFieldChange(data.payload);
                    await onSubmit();
                }
            } catch (error) {
                console.error(error);
            }
        },
        [onFieldChange, onSubmit]
    );

    useEffect(() => {
        window.addEventListener('message', handleMessageReceive);

        return () => {
            window.removeEventListener('message', handleMessageReceive);
        };
    }, [handleMessageReceive]);

    return (
        <Field<ApplicantLoginFormValues['pushCode']>
            name={PUSH_CODE_FIELD}
            type="text"
            render={({ input }) => {
                return (
                    <>
                        <input
                            hidden
                            {...input}
                            value={pushExternalAccessToken}
                            data-qa="applicant-login-input-two-factor-push"
                        />
                        <iframe src={pushRedirectUrl} />
                    </>
                );
            }}
        />
    );
};

export default TwoFactorPushField;
